import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Divider,
  Box,
  Typography,
  Stack,
  Fade,
} from '@mui/material'
import localization from '../utils/localization'
import FormInput from './FormInput'
import ContentItem from './ContentItem'
import UserUtils from '../utils/user'
import ServerUtils from '../utils/server'
import AesUtils from '../utils/aes'
import ContentUtils from '../utils/content'
import ToolsUtils from '../utils/tools'

function ModalTag(props) {
  const [submit, setSubmit] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [selectedContentMap, setSelectedContentMap] = useState({})
  const [tagName, setTagName] = useState(props.tagName)
  const [tagNameErr, setTagNameErr] = useState('')
  const [renderContentList, setRenderContentList] = useState([])

  useEffect(() => {
    if (!props.open) return
    setTagName(props.tagName || '')
    setTagNameErr('')
    let tmpSelectedContentMap = {}
    if (props.contentItemList) {
      for (let i in props.contentItemList) {
        tmpSelectedContentMap[props.contentItemList[i].id] = true
      }
    }
    setSelectedContentMap(tmpSelectedContentMap)
    setRenderContentList(
      props.contentList ? ToolsUtils.sortContentList([...props.contentList], props.sortType) : []
    )
  }, [props.open, props.tagName, props.contentItemList, props.sortType, props.contentList])

  function submitTag() {
    if (submit === true) return
    setSubmit(true)
    let hasErr = false
    if (tagName === '') {
      setTagNameErr(localization.strings.required)
      hasErr = true
    } else if (tagName in props.groupMap && props.tagName !== tagName) {
      setTagNameErr(localization.strings.exists)
      hasErr = true
    }
    if (hasErr) {
      setSubmit(false)
      return
    }
    if (Object.keys(selectedContentMap).filter((k) => selectedContentMap[k]).length <= 0) {
      toast.error(localization.strings.contentRequired)
      hasErr = true
    }
    if (hasErr) {
      setSubmit(false)
      return
    }
    let needUpdateContents = []
    for (let i in props.contentList) {
      let item = props.contentList[i]
      if (item.tags === undefined) {
        item.tags = []
      }
      if (item.tags.includes(tagName) && !selectedContentMap[item.id]) {
        let copyItem = JSON.parse(JSON.stringify(item))
        copyItem.tags = copyItem.tags.filter((tagItem) => tagItem !== tagName)
        needUpdateContents.push(copyItem)
      } else if (!item.tags.includes(tagName) && selectedContentMap[item.id]) {
        let copyItem = JSON.parse(JSON.stringify(item))
        copyItem.tags.push(tagName)
        if (props.tagName && props.tagName !== tagName) {
          copyItem.tags = copyItem.tags.filter((tagItem) => tagItem !== props.tagName)
        }
        needUpdateContents.push(copyItem)
      } else if (
        props.tagName &&
        props.tagName !== tagName &&
        item.tags.includes(props.tagName)
      ) {
        let copyItem = JSON.parse(JSON.stringify(item))
        copyItem.tags = copyItem.tags.filter((tagItem) => tagItem !== props.tagName)
        needUpdateContents.push(copyItem)
      }
    }
    let userInfo = UserUtils.getUserInfo()
    let lastModifyTime = new Date().getTime() / 1000
    ServerUtils.updateContent({
      version: userInfo.version,
      contents: needUpdateContents.map(function (contentItem) {
        return {
          content_id: contentItem.id,
          encrypted_data: AesUtils.encryptData(
            userInfo.account,
            UserUtils.getMasterPass(),
            JSON.stringify({
              title: contentItem.title,
              content: contentItem.content,
              type: contentItem.type,
              color: contentItem.color,
              account: contentItem.account,
              extra: contentItem.extra,
              tags: contentItem.tags,
            })
          ),
        }
      }),
    }).then(async (resp) => {
      setSubmit(false)
      if (!resp.ok) {
        try {
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            toast.error(ServerUtils.handleError(respJson))
            return
          }
        } catch (e) { }
        toast.error(localization.strings.networkErr)
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      ContentUtils.updateContents(
        needUpdateContents.map(function (contentItem) {
          return {
            id: contentItem.id,
            version: userInfo.version,
            title: contentItem.title,
            content: contentItem.content,
            type: contentItem.type,
            color: contentItem.color,
            account: contentItem.account,
            last_modify_time: lastModifyTime,
            extra: contentItem.extra,
            tags: contentItem.tags,
          }
        })
      )
      toast.success(localization.strings.successModify)
      props.onTagName(tagName)
      props.onClose()
      setSelectedContentMap({})
      setTagName('')
    })
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          p: 1,
          backgroundColor: '#F8FAFC',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          textAlign: 'center',
          pt: 4,
          pb: 1,
          color: '#1E293B',
        }}
      >
        {props.title}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 4 }}>
        <Fade in={props.open} timeout={500}>
          <Box sx={{ mt: 1 }}>
            <FormInput
              label={localization.strings.tagName}
              placeholder={localization.strings.inputTagNameHint}
              value={tagName}
              onChange={(event) => {
                setTagName(event.target.value)
                setTagNameErr('')
              }}
              error={tagNameErr}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 3, mb: 4 }}>
              <Button
                fullWidth
                variant="text"
                onClick={props.onClose}
                sx={{
                  borderRadius: '14px',
                  py: 1.5,
                  fontWeight: 700,
                  color: '#64748B',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                }}
              >
                {localization.strings.cancel}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={submitTag}
                sx={{
                  borderRadius: '14px',
                  py: 1.5,
                  fontWeight: 700,
                  backgroundColor: '#1E88E5',
                  boxShadow: '0 8px 20px rgba(30, 136, 229, 0.2)',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#1565C0' },
                }}
              >
                {localization.strings.confirm}
              </Button>
            </Stack>

            <Divider sx={{ mb: 4, opacity: 0.6 }}>
              <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 800 }}>
                {localization.strings.tagAddContent}
              </Typography>
            </Divider>

            <FormInput
              label={localization.strings.keyword}
              placeholder={localization.strings.keywordHint}
              size="small"
              onChange={(event) => setKeyword(event.target.value)}
              sx={{ mb: 2 }}
            />

            <Box
              sx={{
                mt: 2,
                maxHeight: '40vh',
                overflowY: 'auto',
                pr: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '10px',
                },
                paddingTop: '10px',
              }}
            >
              <Stack spacing={1}>
                {renderContentList
                  .filter((item) => {
                    if (keyword === '') return true
                    return item.title.toLowerCase().indexOf(keyword.toLowerCase()) >= 0
                  })
                  .map((item) => (
                    <ContentItem
                      key={item.id}
                      checkBox={true}
                      contentType={item.type}
                      text={item.title}
                      active={selectedContentMap[item.id]}
                      onClick={() => {
                        let tmpMap = Object.assign({}, selectedContentMap)
                        tmpMap[item.id] = !tmpMap[item.id]
                        setSelectedContentMap(tmpMap)
                      }}
                    />
                  ))}
              </Stack>
            </Box>
          </Box>
        </Fade>
      </DialogContent>
    </Dialog>
  )
}

export default ModalTag
